import React from 'react';
import { Server, Phone, Video, KeyRound, Cable, MapPin } from 'lucide-react';

// Public-facing CipherOne service lineup shown on msp.cipherone.us.
// Promotes Physical Access Control and Structured Cabling as first-class
// categories alongside the established IT, VoIP and camera services.
// Residential & Commercial, Greater Houston / Southeast Texas. No prices,
// credentials or capabilities beyond what CipherOne actually offers.
const SERVICES = [
  {
    key: 'managed-it',
    icon: Server,
    name: 'Managed IT',
    blurb: 'Proactive monitoring, helpdesk and management for your computers, servers and networks.',
  },
  {
    key: 'voip',
    icon: Phone,
    name: 'VoIP Phone Systems',
    blurb: 'Modern cloud calling for offices and homes, with the features your day depends on.',
  },
  {
    key: 'cameras',
    icon: Video,
    name: 'Security Cameras',
    blurb: 'Professionally installed video surveillance to keep an eye on what matters.',
  },
  {
    key: 'access-control',
    icon: KeyRound,
    name: 'Physical Access Control',
    blurb: 'Door entry, keypad, card and fob systems that control who can come through the door.',
  },
  {
    key: 'structured-cabling',
    icon: Cable,
    name: 'Structured Cabling',
    blurb: 'Network and data cabling — clean drops, proper termination, labeling and tidy infrastructure.',
  },
];

export const ServicesHighlight = () => (
  <section
    className="co-scope w-full max-w-5xl mx-auto mt-10 relative z-10"
    aria-label="CipherOne services"
    data-testid="services-highlight"
  >
    <div className="text-center px-4">
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#37c9ee]">What we do</p>
      <h2 className="text-lg md:text-xl font-semibold text-[#f4f8fa] mt-1">
        Technology &amp; infrastructure for home and business
      </h2>
      <p className="text-sm text-[#b1c3ce] mt-1 flex items-center justify-center gap-1.5">
        <MapPin className="h-3.5 w-3.5 text-[#37c9ee]" aria-hidden="true" />
        Serving Greater Houston &amp; Southeast Texas &mdash; Residential &amp; Commercial
      </p>
    </div>

    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 px-4">
      {SERVICES.map(({ key, icon: Icon, name, blurb }) => (
        <div
          key={key}
          data-testid={`service-card-${key}`}
          className="rounded-lg border border-[#193b4b] bg-[#081a25]/70 p-4 transition-colors duration-200 hover:border-[#37c9ee]/60"
        >
          <div className="flex items-center gap-2.5">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[#193b4b] text-[#37c9ee]">
              <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
            </span>
            <h3 className="text-sm font-semibold text-[#f4f8fa]">{name}</h3>
          </div>
          <p className="text-xs text-[#b1c3ce] mt-2 leading-relaxed">{blurb}</p>
          <span className="inline-block text-[10px] uppercase tracking-wider text-[#5f7585] mt-3">
            Residential &amp; Commercial
          </span>
        </div>
      ))}
      <div className="rounded-lg border border-dashed border-[#193b4b] bg-transparent p-4 flex flex-col justify-center">
        <p className="text-xs text-[#b1c3ce] leading-relaxed">
          One trusted partner for your IT, phones, cameras, door access and cabling.
        </p>
        <a
          href="https://cipherone.us/services"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm font-semibold text-[#37c9ee] hover:underline mt-2"
          data-testid="services-view-all"
        >
          View all services &rarr;
        </a>
      </div>
    </div>
  </section>
);

export default ServicesHighlight;
